import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TravelAssociatesModal from './TravelAssociatesModal';
import EditClientModal from './EditClientModal';
import EditAssociateModal from './EditAssociateModal';
import './DetailComponents.css';
import API_BASE_URL from '../config';

const ClientDetails = ({ clientId, isDashboard = false }) => {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editingAssociates, setEditingAssociates] = useState(false);
  const [editingAssociateIndex, setEditingAssociateIndex] = useState(null);
  const [editedClient, setEditedClient] = useState({});
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/clients/${clientId}`)
      .then(response => {
        setClient(response.data.data);
        setEditedClient(response.data.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [clientId]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEdit = () => setEditing(true);

  const handleClickOutside = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setShowMenu(false);
    }
  };

  const handleSave = (updatedClient) => {
    axios.put(`${API_BASE_URL}/api/clients/${clientId}`, updatedClient)
      .then(response => {
        setClient(response.data.data);
        setEditedClient(response.data.data);
        setEditing(false);
      })
      .catch(error => console.error('Error updating client:', error));
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this client and their user account?')) {
      axios.delete(`${API_BASE_URL}/api/clients/${clientId}`)
        .then(() => navigate('/clients'))
        .catch(() => alert('Failed to delete client. Please try again.'));
    }
  };

  const handleSaveAssociate = (newAssociate) => {
    const updatedAssociates = [...(client.travelAssociates || [])];
    const newId = updatedAssociates.length > 0 
      ? Math.max(...updatedAssociates.map(assoc => assoc.id)) + 1 
      : 1;
    updatedAssociates.push({ ...newAssociate, id: newId });
    axios.put(`${API_BASE_URL}/api/clients/${clientId}`, { ...client, travelAssociates: updatedAssociates })
      .then(response => {
        setClient(response.data.data);
        setEditedClient(response.data.data);
        setEditingAssociates(false);
      })
      .catch(() => {});
  };

  const handleEditAssociate = (index) => setEditingAssociateIndex(index);

  const handleSaveEditedAssociate = (updatedAssociate, index) => {
    const updatedAssociates = [...client.travelAssociates];
    updatedAssociates[index] = updatedAssociate;
    axios.put(`${API_BASE_URL}/api/clients/${clientId}`, { ...client, travelAssociates: updatedAssociates })
      .then(response => {
        setClient(response.data.data);
        setEditedClient(response.data.data);
        setEditingAssociateIndex(null);
      })
      .catch(error => console.error('Error updating associate:', error));
  };

  const handleDeleteAssociate = (index) => {
    if (window.confirm('Are you sure you want to delete this travel associate?')) {
      const updatedAssociates = client.travelAssociates.filter((_, i) => i !== index);
      axios.put(`${API_BASE_URL}/api/clients/${clientId}`, { ...client, travelAssociates: updatedAssociates })
        .then(response => {
          setClient(response.data.data);
          setEditedClient(response.data.data);
        })
        .catch(error => console.error('Error deleting associate:', error));
    }
  };

  const handleBack = () => navigate('/clients');

  const toggleMenu = () => setShowMenu(!showMenu);

  if (loading) return <div>Loading client details...</div>;
  if (!client) return <div>Client not found</div>;

  return (
    <div className={isDashboard ? 'dashboard-integrated' : ''}>
      <div className={`App client-details ${isDashboard ? 'dashboard-mode' : ''}`}>
        {!isDashboard && <h1>Client Profile</h1>}
        {!isDashboard && (
          <div className="header-section">
            <button className="back-button" onClick={handleBack}>&lt;</button>
          </div>
        )}
        <div className={`info-box client-details-box ${isDashboard ? 'dashboard-style' : ''}`} style={{ maxWidth: isDashboard ? '100%' : '700px !important', margin: isDashboard ? '0 auto' : '20px auto' }}>
          <div className={`details-actions client-actions ${isDashboard ? 'dashboard-actions' : ''}`} ref={menuRef}>
            {isDashboard ? (
              <div className="dashboard-action-buttons">
                <button className="dashboard-action-btn" onClick={handleEdit}>
                  Edit Client
                </button>
                <button className="dashboard-action-btn" onClick={() => setEditingAssociates(true)}>
                  Add Associate
                </button>
                <button className="dashboard-action-btn danger" onClick={handleDelete}>
                  Delete Client
                </button>
              </div>
            ) : (
              <>
                <button className="actions-menu-button" onClick={toggleMenu}>⋮</button>
                {showMenu && (
                  <div className="actions-menu">
                    <div className="actions-menu-item" onClick={() => { handleEdit(); setShowMenu(false); }}>Edit</div>
                    <div className="actions-menu-item" onClick={() => { handleDelete(); setShowMenu(false); }}>Delete</div>
                    <div className="actions-menu-item" onClick={() => { setEditingAssociates(true); setShowMenu(false); }}>Add Associate</div>
                  </div>
                )}
              </>
            )}
          </div>
          
          <h2>{client.name || ''} {client.midName || ''} {client.lastName1 || ''} {client.lastName2 || ''}</h2>
        <div className="details-box">
          <div className="details-grid">
            {/* Single Column */}
            <div className="details-column">
              {/* Client ID and Email on same line */}
              <div className="details-row">
                <div className="details-item">
                  <span className="label">Client ID:</span> {client._id}
                </div>
                <div className="details-item">
                  <span className="label">Email:</span> {client.email || 'N/A'}
                </div>
              </div>

              {/* Date of Birth and Trips on same line */}
              <div className="details-row">
                <div className="details-item">
                  <span className="label">Date of Birth:</span> {client.dob ? new Date(client.dob).toLocaleDateString() : 'N/A'}
                </div>
                <div className="details-item">
                  <span className="label">Trips:</span> {client.noOfTrips || 0}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="details-section travel-associates">
          <div className="details-section-title">
            <h3>Travel Associates</h3>
          </div>
          {client.travelAssociates && client.travelAssociates.length > 0 ? (
            <ul>
              {client.travelAssociates.map((associate, index) => (
                <li key={index}>
                  <span>{associate.name} {associate.relationship ? `(${associate.relationship})` : ''}</span>
                  <div style={{ display: 'inline-flex', gap: '10px' }}>
                    <button className="edit-button" onClick={() => handleEditAssociate(index)}>✎</button>
                    <button className="delete-button" onClick={() => handleDeleteAssociate(index)}>X</button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p>No travel associates added</p>
          )}
        </div>
      </div>
      {editing && (
        <EditClientModal 
          client={editedClient} 
          onSave={handleSave} 
          onClose={() => setEditing(false)} 
        />
      )}
      {editingAssociates && (
        <TravelAssociatesModal 
          onSave={handleSaveAssociate} 
          onClose={() => setEditingAssociates(false)} 
        />
      )}
      {editingAssociateIndex !== null && (
        <EditAssociateModal 
          associate={client.travelAssociates[editingAssociateIndex]} 
          index={editingAssociateIndex}
          onSave={handleSaveEditedAssociate} 
          onClose={() => setEditingAssociateIndex(null)} 
        />
      )}
      </div>
    </div>
  );
};

export default ClientDetails;